'use client'

import { useAuthorization } from '@entities/authentication'
import {
  tutorConnectEntityQueries,
  useFavoriteTutorMutation,
  useUnfavoriteTutorMutation,
} from '@entities/tutor-connect'
import { MAX_BLOCKED_TUTORS, SLOT_FULL_DIALOG, useExclusionListQuery } from '@entities/tutor-exclusion'
import { useBlockTutor } from '@features/tutor-exclusion'
import type { NpsReviewState } from '@server/domains/lesson-review/dto'
import { track } from '@shared/analytics'
import { useTranslation } from '@shared/config/i18n'
import { EVENT_IDS } from '@shared/constants'
import { useToast } from '@shared/hooks'
import { openAlertDialog } from '@shared/ui'
import { useQuery } from '@tanstack/react-query'
import { useRouter } from 'next/navigation'
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'

import { Button, VStack } from '@podo-app/design-system-temp'

import { useNpsSkip, useNpsSubmit, useNpsUpdateFeedback, useNpsUpdateWantBlock } from '../../hooks'
import { clearNpsDraft, readNpsDraft, restoreNpsSurvey, writeNpsDraft, type NpsFunnelStep } from '../../lib/nps-draft'
import { NpsCompleteStep } from './nps-complete-step'
import { NpsRatingStep } from './nps-rating-step'
import { NpsReasonStep } from './nps-reason-step'
import { NPS_HIGH_RATING_THRESHOLD, NPS_LOW_RATING_THRESHOLD } from './nps-survey.constants'
import type {
  LessonTutorSummary,
  NpsRating,
  NpsRatingVariant,
  NpsSubmitSource,
  NpsSurveyState,
} from './nps-survey.types'

const trackNpsEvent: typeof track = (eventName, properties) => {
  try {
    track(eventName, properties)
  } catch {
    // Analytics must not change a saved result, survey navigation or pending state.
  }
}

const getReturnUrl = (referrer: string): string => {
  switch (referrer) {
    case 'reserved':
      return '/reservation'
    case 'ai-home':
      return '/home/ai'
    case 'home':
      return '/home'
    case 'lesson-list':
    case 'booking':
    default:
      return '/subscribes'
  }
}

interface NpsSurveyFlowProps {
  classId: number
  tutorId: number
  referrer: string
  completionUrl?: string
  lessonTutorSummary?: LessonTutorSummary
  npsRatingVariant?: NpsRatingVariant
  npsSubmitSource?: NpsSubmitSource
  initialReviewState?: NpsReviewState | null
}

export const NpsSurveyFlow = ({
  classId,
  tutorId: clientTutorId,
  referrer,
  completionUrl,
  lessonTutorSummary,
  npsRatingVariant = 'legacy',
  npsSubmitSource = 'IN_FLOW',
  initialReviewState,
}: NpsSurveyFlowProps) => {
  const t = useTranslation()
  const returnUrl = completionUrl ?? getReturnUrl(referrer)
  const tutorId = initialReviewState?.tutorId ?? clientTutorId
  const initial = restoreNpsSurvey(initialReviewState)
  const [step, setStep] = useState<NpsFunnelStep>(initial.step)
  const [state, setState] = useState<NpsSurveyState>(initial.state)
  const [isSaving, setIsSaving] = useState(false)
  const savingRef = useRef(false)
  const requestGeneration = useRef(0)
  const leavingRef = useRef(false)
  const [draftReady, setDraftReady] = useState(false)
  const draftStoredRef = useRef(false)
  const unsavedInputRef = useRef(false)
  const persistedRef = useRef(initial.state)
  const favoriteInitialized = useRef(false)
  const canContinue =
    initialReviewState?.classId === classId &&
    initialReviewState.tutorId > 0 &&
    (initialReviewState.status === 'SUBMITTED' ||
      (initialReviewState.canWrite && (initialReviewState.status === null || initialReviewState.status === 'SKIPPED')))

  const { bearerToken } = useAuthorization()
  const { toast } = useToast()
  const { mutateAsync: submitNps, isError: hasSubmitError } = useNpsSubmit()
  const { mutateAsync: skipNps } = useNpsSkip()
  const {
    mutateAsync: updateFeedback,
    isError: hasFeedbackError,
    isPending: isFeedbackPending,
  } = useNpsUpdateFeedback()
  const { mutateAsync: updateWantBlock } = useNpsUpdateWantBlock()
  const { mutateAsync: blockTutor } = useBlockTutor({ location: 'nps_survey' })
  const { mutateAsync: favoriteTutor } = useFavoriteTutorMutation(bearerToken)
  const { mutateAsync: unfavoriteTutor } = useUnfavoriteTutorMutation(bearerToken)
  const { data: exclusionData } = useExclusionListQuery()
  const { data: favoritesData } = useQuery(tutorConnectEntityQueries.favorites(bearerToken))
  const router = useRouter()

  useEffect(() => {
    const generation = ++requestGeneration.current
    leavingRef.current = false
    savingRef.current = false
    setIsSaving(false)
    return () => {
      requestGeneration.current = generation + 1
    }
  }, [classId, initialReviewState])

  useEffect(() => {
    if (initialReviewState && canContinue) {
      const draft = readNpsDraft(initialReviewState)
      const restored = restoreNpsSurvey(initialReviewState, draft)
      favoriteInitialized.current = draft?.step === 'complete' && draft.state.rating === initialReviewState.rating
      setStep(restored.step)
      setState(restored.state)
      persistedRef.current = restoreNpsSurvey(initialReviewState).state
    } else if (
      initialReviewState?.classId === classId &&
      (initialReviewState.status === 'DELETED' || initialReviewState.status === 'BLOCKED')
    ) {
      clearNpsDraft(initialReviewState)
    }
    setDraftReady(true)
  }, [initialReviewState, canContinue, classId])

  useEffect(() => {
    if (!initialReviewState || !canContinue || !draftReady || leavingRef.current) {
      return
    }
    // Failed or pending acknowledgements can leave even empty input newer than the confirmed local state.
    const feedbackPending =
      step === 'reason' &&
      (hasFeedbackError ||
        isFeedbackPending ||
        JSON.stringify(state.reasons) !== JSON.stringify(persistedRef.current.reasons) ||
        state.reasonText !== persistedRef.current.reasonText)
    draftStoredRef.current = writeNpsDraft(initialReviewState, step, state, feedbackPending)
    unsavedInputRef.current = feedbackPending || JSON.stringify(state) !== JSON.stringify(persistedRef.current)
  }, [initialReviewState, canContinue, draftReady, step, state, hasFeedbackError, isFeedbackPending])

  useEffect(() => {
    const beforeUnload = (event: BeforeUnloadEvent) => {
      if (!leavingRef.current && !draftStoredRef.current && unsavedInputRef.current) {
        event.preventDefault()
        event.returnValue = ''
      }
    }
    window.addEventListener('beforeunload', beforeUnload)
    return () => window.removeEventListener('beforeunload', beforeUnload)
  }, [])

  const leaveSurvey = useCallback(() => {
    leavingRef.current = true
    if (initialReviewState) {
      clearNpsDraft(initialReviewState)
    }
    window.location.href = returnUrl
  }, [initialReviewState, returnUrl])

  const isAlreadyFavorited = favoritesData?.some((f) => f.tutorId === tutorId && f.favorite) ?? false
  const isBlocked = exclusionData?.blockedTutors?.some((t) => t.tutorId === tutorId) ?? false

  // Initialize wantFavorite once when favorites resolve and rating is high enough.
  // A ref guard prevents re-initialization after user toggles the checkbox.
  useEffect(() => {
    if (favoriteInitialized.current) {
      return
    }
    if (favoritesData === undefined) {
      return
    }
    if (state.rating !== null && state.rating >= NPS_HIGH_RATING_THRESHOLD) {
      favoriteInitialized.current = true
      if (isAlreadyFavorited) {
        persistedRef.current = { ...persistedRef.current, wantFavorite: true }
        setState((prev) => ({ ...prev, wantFavorite: true }))
      }
    }
  }, [favoritesData, isAlreadyFavorited, state.rating])

  useEffect(() => {
    if (canContinue) {
      trackNpsEvent(EVENT_IDS.NPS_SURVEY_VIEWED, { classId, tutorId })
    }
  }, [classId, tutorId, canContinue])

  const handleSkip = useCallback(async () => {
    if (savingRef.current) {
      return
    }
    savingRef.current = true
    setIsSaving(true)
    const generation = requestGeneration.current
    try {
      // A reason-step skip preserves the rating that was already acknowledged.
      if (step === 'rating') {
        await skipNps({ classId, source: npsSubmitSource })
      }
      if (generation !== requestGeneration.current) {
        return
      }
      trackNpsEvent(EVENT_IDS.NPS_SURVEY_SKIPPED, { step, classId, tutorId })
    } catch {
      // The survey is optional: leave on an explicit skip without claiming it was recorded.
    } finally {
      if (generation === requestGeneration.current) {
        leaveSurvey()
      }
    }
  }, [step, classId, tutorId, skipNps, npsSubmitSource, leaveSurvey])

  const handleRatingNext = useCallback(async () => {
    if (state.rating === null || savingRef.current) {
      return
    }
    savingRef.current = true
    setIsSaving(true)
    const generation = requestGeneration.current
    try {
      await submitNps({
        classId,
        tutorId,
        rating: state.rating,
        reasons: [],
        reasonText: '',
        wantBlock: false,
        source: npsSubmitSource,
      })
      if (generation !== requestGeneration.current) {
        return
      }
      persistedRef.current = { ...persistedRef.current, rating: state.rating }
      trackNpsEvent(EVENT_IDS.NPS_RATING_SUBMITTED, { rating: state.rating, classId, tutorId })
      setStep('reason')
    } catch {
      // The mutation shows the failure toast; keep the rating for an explicit retry.
    } finally {
      if (generation === requestGeneration.current) {
        savingRef.current = false
        setIsSaving(false)
      }
    }
  }, [state.rating, submitNps, classId, tutorId, npsSubmitSource])

  const handleReasonNext = useCallback(async () => {
    if (state.rating === null || savingRef.current) {
      return
    }
    savingRef.current = true
    setIsSaving(true)
    const generation = requestGeneration.current
    try {
      await updateFeedback({
        classId,
        reasons: state.reasons,
        reasonText: state.reasonText,
      })
      if (generation !== requestGeneration.current) {
        return
      }
      persistedRef.current = { ...persistedRef.current, reasons: state.reasons, reasonText: state.reasonText }
      trackNpsEvent(EVENT_IDS.NPS_FEEDBACK_SUBMITTED, {
        rating: state.rating,
        reasons: state.reasons,
        hasReasonText: Boolean(state.reasonText),
        classId,
        tutorId,
      })
      setStep('complete')
    } catch {
      // The mutation shows the failure toast; keep the selected feedback.
    } finally {
      if (generation === requestGeneration.current) {
        savingRef.current = false
        setIsSaving(false)
      }
    }
  }, [state.rating, updateFeedback, classId, state.reasons, state.reasonText, tutorId])

  // 칩 분기: 4★ 이상만 긍정, 3★ 이하는 부정(중립 3★ 포함) — PRD §3.8
  const isPositiveRating = state.rating !== null && state.rating >= NPS_HIGH_RATING_THRESHOLD

  const handleToggleReason = useCallback((reason: string) => {
    setState((prev) => ({
      ...prev,
      reasons: prev.reasons.includes(reason) ? prev.reasons.filter((r) => r !== reason) : [...prev.reasons, reason],
    }))
  }, [])

  const handleComplete = useCallback(async () => {
    if (state.rating === null || savingRef.current) {
      return
    }
    savingRef.current = true
    setIsSaving(true)
    const generation = requestGeneration.current
    let navigationStarted = false
    try {
      const shouldFavorite =
        state.wantFavorite && !isAlreadyFavorited && !isBlocked && state.rating >= NPS_HIGH_RATING_THRESHOLD
      const shouldBlock = state.wantBlock && !isBlocked && state.rating <= NPS_LOW_RATING_THRESHOLD
      if ((shouldFavorite || shouldBlock) && (favoritesData === undefined || exclusionData === undefined)) {
        toast({ title: t('lessonReview.nps.errors.submitFailed') })
        return
      }
      await updateWantBlock({
        classId,
        wantBlock: state.wantBlock,
      })
      if (generation !== requestGeneration.current) {
        return
      }
      persistedRef.current = { ...persistedRef.current, wantBlock: state.wantBlock }

      // Only call favoriteTutor when not already favorited to keep the action idempotent.
      if (shouldFavorite) {
        await favoriteTutor(tutorId).catch((error) => {
          toast({ title: t('lessonReview.nps.errors.submitFailed') })
          throw error
        })
        if (generation !== requestGeneration.current) {
          return
        }
      }

      if (shouldBlock) {
        const isSlotFull = (exclusionData?.currentCount ?? 0) >= MAX_BLOCKED_TUTORS
        if (isSlotFull) {
          openAlertDialog({
            title: (
              <span className="text-[18px] font-bold leading-[26px] tracking-[-0.36px] text-[#1C1C1C]">
                {t('tutorExclusion.slotFullDialog.title')}
              </span>
            ),
            description: (
              <span className="text-[16px] font-medium leading-6 tracking-[-0.32px] text-[#757575]">
                {t('tutorExclusion.slotFullDialog.description', { count: MAX_BLOCKED_TUTORS })}
              </span>
            ),
            confirmMessage: t('tutorExclusion.slotFullDialog.confirm'),
            redirectPath: SLOT_FULL_DIALOG.REDIRECT_PATH,
            router,
          })
          return
        }
        // Unset favorite before blocking so the two states stay mutually exclusive.
        if (isAlreadyFavorited) {
          await unfavoriteTutor(tutorId).catch((error) => {
            toast({ title: t('lessonReview.nps.errors.submitFailed') })
            throw error
          })
          if (generation !== requestGeneration.current) {
            return
          }
        }
        await blockTutor({ tutorId, classId })
        if (generation !== requestGeneration.current) {
          return
        }
      }
      trackNpsEvent(EVENT_IDS.NPS_SURVEY_COMPLETED, {
        rating: state.rating,
        reasons: state.reasons,
        wantBlock: state.wantBlock,
        classId,
        tutorId,
      })
      leaveSurvey()
      navigationStarted = true
    } catch {
      // Each mutation reports its error; do not claim completion or leave on failure.
    } finally {
      if (!navigationStarted && generation === requestGeneration.current) {
        savingRef.current = false
        setIsSaving(false)
      }
    }
  }, [
    state,
    updateWantBlock,
    blockTutor,
    favoriteTutor,
    unfavoriteTutor,
    classId,
    tutorId,
    exclusionData,
    isAlreadyFavorited,
    isBlocked,
    router,
    t,
    toast,
    leaveSurvey,
    favoritesData,
  ])

  if (!canContinue) {
    return (
      <VStack className="h-dvh items-center justify-center gap-5 p-5" data-testid="nps-unavailable">
        <p>{t(initialReviewState ? 'lessonReview.nps.errors.unavailable' : 'lessonReview.nps.errors.loadFailed')}</p>
        {(!initialReviewState || initialReviewState.status === null || initialReviewState.status === 'SKIPPED') && (
          <Button onClick={() => router.refresh()}>{t('common.actions.retry')}</Button>
        )}
        <Button onClick={leaveSurvey}>{t('common.actions.confirm')}</Button>
      </VStack>
    )
  }

  let content: ReactNode
  switch (step) {
    case 'rating':
      content = (
        <NpsRatingStep
          value={state.rating}
          onChange={(rating: NpsRating) => setState((prev) => ({ ...prev, rating }))}
          onNext={handleRatingNext}
          onSkip={handleSkip}
          lessonTutorSummary={clientTutorId === tutorId ? lessonTutorSummary : undefined}
          npsRatingVariant={npsRatingVariant}
          onRecover={hasSubmitError ? () => router.refresh() : undefined}
        />
      )
      break
    case 'reason':
      content = (
        <NpsReasonStep
          variant={isPositiveRating ? 'positive' : 'negative'}
          selectedReasons={state.reasons}
          reasonText={state.reasonText}
          onToggleReason={handleToggleReason}
          onChangeReasonText={(reasonText) => setState((prev) => ({ ...prev, reasonText }))}
          onSubmit={handleReasonNext}
          onSkip={handleSkip}
        />
      )
      break
    case 'complete':
      content = (
        <NpsCompleteStep
          rating={state.rating!}
          wantBlock={state.wantBlock}
          onToggleBlock={() => setState((prev) => ({ ...prev, wantBlock: !prev.wantBlock }))}
          wantFavorite={state.wantFavorite}
          onToggleFavorite={() => setState((prev) => ({ ...prev, wantFavorite: !prev.wantFavorite }))}
          onComplete={handleComplete}
          canFavorite={
            state.rating !== null &&
            state.rating >= NPS_HIGH_RATING_THRESHOLD &&
            !isBlocked &&
            (state.wantFavorite || (favoritesData !== undefined && exclusionData !== undefined))
          }
          canBlock={
            state.rating !== null &&
            state.rating <= NPS_LOW_RATING_THRESHOLD &&
            !isBlocked &&
            (state.wantBlock || (favoritesData !== undefined && exclusionData !== undefined))
          }
        />
      )
      break
  }
  return (
    <fieldset disabled={isSaving} className="min-w-0">
      {content}
    </fieldset>
  )
}
